import React, {useState, useCallback} from 'react';
import {Table, Button} from 'antd';
import {FolderOutlined, DownOutlined, RightOutlined, DownloadOutlined, LockOutlined} from '@ant-design/icons';
import {TreeNode} from '../../../../store/modelFile';
import {ModelDTO, UserType} from 'hemwb-api';
import type {ColumnsType} from 'antd/es/table';
import {getFileIcon} from '../../../../utils/fileIcons';
import {useCurrentUser} from '../../../../store/auth';
import {isModelGlobal} from '../../../../store/model';
import styles from './FileTreeNode.module.scss';

interface FileTreeNodeTHProps {
  files: TreeNode[];
  node?: TreeNode;
  isRoot?: boolean;
  defaultFolderOpen?: boolean;
  onDownloadFile: (fileUuid: string, fileName: string) => void;
  onManageAccess: (fileUuid: string, fileName: string) => void;
  onRequestAccess: (fileUuid: string, fileName: string) => void;
  onManageOwnership: (fileUuid: string, fileName: string) => void;
  model: ModelDTO | null;
  level?: number;
}

interface FileRowData extends TreeNode {
  updatedOn?: Date;
  updatedBy?: string;
  fileTag?: string;
  children?: TreeNode[];
  owner: {isid: string; firstname: string; lastname: string};
  cdd: string;
  userAccess: boolean;
  isPrivate: boolean;
}

export const FileTreeNodeTH: React.FC<FileTreeNodeTHProps> = ({
  files,
  node,
  isRoot = false,
  defaultFolderOpen = true,
  onDownloadFile,
  onManageAccess,
  onRequestAccess,
  onManageOwnership,
  model,
  level = 0,
}) => {
  const [isOpen, setIsOpen] = useState(isRoot || defaultFolderOpen === true);
  const {id: currentUserId, userType: loggedInUserType} = useCurrentUser();

  const handleToggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  // Check if user can download a THEOREM file based on CDD and access
  const canDownloadFile = useCallback(
    (file: FileRowData): boolean => {
      const isGlobalModel = model ? isModelGlobal(model) : false;

      // Private files - cannot download
      if (file.isPrivate) {
        return false;
      }

      // Global Model OR No CDD - can always download
      if (isGlobalModel || file.cdd === 'No') {
        return true;
      }

      // CDD = Yes - can download only if user has access
      if (file.cdd === 'Yes') {
        return !!file.userAccess;
      }

      return true; // Default: allow download
    },
    [model],
  );

  // Get the appropriate access button/icon based on THEOREM CDD rules
  const getAccessButton = useCallback(
    (file: FileRowData) => {
      const isGlobalModel = model ? isModelGlobal(model) : false;

      // 1. Private files - show lock icon only
      if (file.isPrivate) {
        return <LockOutlined style={{color: '#999'}} />;
      }

      // 2. Super User - Always gets "Manage File Access" button
      if (UserType.SUPERUSER === loggedInUserType) {
        return (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onManageOwnership(file.uuid, file.name);
            }}
            style={{padding: 0, height: 'auto'}}
          >
            Manage File Access
          </Button>
        );
      }

      // 3. File Owner - Gets "Manage File Access" button
      if (file.owner?.isid === currentUserId) {
        return (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onManageAccess(file.uuid, file.name);
            }}
            style={{padding: 0, height: 'auto'}}
          >
            Manage File Access
          </Button>
        );
      }

      // 4. Global Model OR No CDD - Show nothing (download is available)
      if (isGlobalModel || file.cdd === 'No') {
        return null;
      }

      // 5. CDD = Yes + No Access - Show "Request Access" button
      if (!file.userAccess && file.cdd === 'Yes') {
        return (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRequestAccess(file.uuid, file.name);
            }}
            style={{padding: 0, height: 'auto'}}
          >
            Request Access
          </Button>
        );
      }

      // 6. CDD = Yes + Has Access - Show nothing (user can download)
      if (file.userAccess && file.cdd === 'Yes') {
        return null;
      }

      return null;
    },
    [onManageAccess, onRequestAccess, onManageOwnership, loggedInUserType, currentUserId, model],
  );

  // Determine which data to work with
  let directories: TreeNode[] = [];
  let childrenFiles: FileRowData[] = [];
  let folderName = '';

  if (isRoot) {
    // Root level: process the files array
    files.forEach((item) => {
      if (item.folder) {
        directories.push(item);
      } else {
        childrenFiles.push(item as FileRowData);
      }
    });
  } else if (node) {
    // Subfolder: use the children array from the node
    folderName = node.name;
    const nodePath = (node as any).path || node.name;
    const nodeChildren = (node as any).children as TreeNode[] | undefined;

    if (nodeChildren) {
      // Filter to get only IMMEDIATE children (not all descendants)
      const immediateChildren = nodeChildren.filter((item: any) => {
        const itemPath = item.path || item.name;
        const relativePath = itemPath.startsWith(nodePath)
          ? itemPath.substring(nodePath.length).replace(/^\//, '')
          : itemPath;
        return !relativePath.includes('/') || relativePath === item.name;
      });

      immediateChildren.forEach((item) => {
        if (item.folder) {
          directories.push(item);
        } else {
          childrenFiles.push(item as FileRowData);
        }
      });
    }
  }

  // Sort folders and files alphabetically by name
  directories.sort((a, b) => a.name.localeCompare(b.name));
  childrenFiles.sort((a, b) => a.name.localeCompare(b.name));

  // Table columns with THEOREM-specific Access column
  const columns: ColumnsType<FileRowData> = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (name: string) => (
        <span className={styles.fileName}>
          {getFileIcon(name, styles.fileIcon)}
          {name}
        </span>
      ),
    },
    {
      title: 'Updated On',
      dataIndex: 'updatedOn',
      key: 'updatedOn',
      render: (date: Date | string) => {
        if (!date) return '';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
    },
    {
      title: 'File Type',
      dataIndex: 'fileTag',
      key: 'fileTag',
    },
    {
      title: 'Access',
      key: 'access',
      width: 150,
      align: 'center',
      render: (_: any, record: FileRowData) => getAccessButton(record),
    },
    {
      title: 'Download',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_: any, record: FileRowData) => {
        const canDownload = canDownloadFile(record);

        return (
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDownloadFile(record.uuid, record.name);
            }}
            disabled={!canDownload}
            className={styles.downloadButton}
            style={canDownload ? undefined : {opacity: 0.3, cursor: 'not-allowed'}}
          />
        );
      },
    },
  ];

  const ArrowIcon = isOpen ? DownOutlined : RightOutlined;

  return (
    <>
      {/* Folder header (not for root) */}
      {!isRoot && node && (
        <button type="button" className={styles.folderHeader} onClick={handleToggleOpen} data-level={level}>
          <FolderOutlined className={styles.folderIcon} />
          <span className={styles.folderName}>{folderName}</span>
          <ArrowIcon className={styles.arrowIcon} />
        </button>
      )}

      {/* Expanded folder content */}
      {isOpen && (
        <div
          className={isRoot ? undefined : styles.folderContent}
          data-level={level}
          data-single-child={directories.length === 1 && childrenFiles.length === 0 ? 'true' : undefined}
        >
          {/* Nested folders */}
          {directories.map((childNode) => (
            <FileTreeNodeTH
              key={childNode.uuid}
              files={files}
              node={childNode}
              defaultFolderOpen={defaultFolderOpen}
              onDownloadFile={onDownloadFile}
              onManageAccess={onManageAccess}
              onRequestAccess={onRequestAccess}
              onManageOwnership={onManageOwnership}
              model={model}
              level={level + 1}
            />
          ))}

          {/* Files table */}
          {childrenFiles.length > 0 && (
            <Table
              dataSource={childrenFiles}
              columns={columns}
              rowKey="uuid"
              pagination={false}
              size="small"
              className={styles.filesTable}
            />
          )}
        </div>
      )}
    </>
  );
};
